import { lazy } from "react";

import { Navigate } from "react-router-dom";

import { AppPagePaths } from "App/routes/paths";

import { agentDesktopSettingsRoute } from "./Pages/AgentDesktop/Routes";
import { agentSettingsRoute } from "./Pages/AgentSettings/Pages/AgentSettingsRoutes";
import { brandSecretsRoute } from "./Pages/BrandSecrets/BrandSecretsRoutes";
import { callBrandingPathResources } from "./Pages/CallBranding/Pages/CallBrandingPaths";
import { complianceSettingsRoute } from "./Pages/ComplianceSettings/ComplianceSettingsRoutes";
import { eventManagementRoute } from "./Pages/DataManagement/EventManagement/Routes";
import { sipRoute } from "./Pages/sip/Pages/SipRoutes";
import { TrustPaths } from "./Pages/Trust/paths";
import { trustRoute } from "./Pages/Trust/TrustRoutes";
import { userManagementRoute } from "./Pages/UserManagement/UserManagementRoutes";
import { SettingsPaths } from "./paths";
import SettingsRoutingPage, { SettingsRerouter } from "./SettingsRoutingPage";

import type { RegalRouteObject } from "App/routes/types";

const Recordings = lazy(() => import("./Pages/Recordings/Recordings"));
const GeneralSettings = lazy(() => import("./Pages/GeneralSettings/GeneralSettings"));
const ExternalTransferPhonebook = lazy(() => import("./Pages/ExternalTransferPhonebook/ExternalTransferPhonebook"));
const Integrations = lazy(() => import("./Pages/Integrations/Integrations"));
const PhoneNumbers = lazy(() => import("./Pages/PhoneNumbers/PhoneNumbers"));
const DataManagement = lazy(() => import("./Pages/DataManagement/DataManagement"));
const YourData = lazy(() => import("./Pages/DataManagement/YourData/YourData"));
const CustomObjectDefinition = lazy(
    () => import("./Pages/DataManagement/CustomObjectDefinition/CustomObjectDefinition")
);
const ContactLists = lazy(() => import("./Pages/ContactLists/ContactLists"));

const callBrandingRedirects = [
    {
        path: SettingsPaths.callBranding,
        element: (
            <Navigate to={`${AppPagePaths.settings}/${SettingsPaths.trust}/${TrustPaths.trustOverview}`} replace />
        ),
    },
    {
        path: `${SettingsPaths.callBranding}/${callBrandingPathResources.businessProfiles}`,
        element: (
            <Navigate to={`${AppPagePaths.settings}/${SettingsPaths.trust}/${TrustPaths.businessProfiles}`} replace />
        ),
    },
    {
        path: `${SettingsPaths.callBranding}/${callBrandingPathResources.phoneNumbers}`,
        element: (
            <Navigate to={`${AppPagePaths.settings}/${SettingsPaths.trust}/${TrustPaths.brandingAndSpam}`} replace />
        ),
    },
];

const commonChildren = [
    {
        index: true,
        element: <Navigate to={`${AppPagePaths.settings}/${SettingsPaths.generalSettings}`} replace />,
    },
    complianceSettingsRoute,
    agentSettingsRoute,
    trustRoute,
    ...callBrandingRedirects,
    sipRoute,
    eventManagementRoute,
    userManagementRoute,
    agentDesktopSettingsRoute,
    {
        path: SettingsPaths.generalSettings,
        element: <GeneralSettings />,
    },
    {
        path: SettingsPaths.externalTransferPhonebook,
        element: <ExternalTransferPhonebook />,
    },
    {
        path: SettingsPaths.contactLists,
        element: <ContactLists />,
    },
    brandSecretsRoute,
    {
        path: SettingsPaths.phoneNumbers,
        element: <PhoneNumbers />,
    },
    {
        path: SettingsPaths.recordings,
        element: <Recordings />,
    },
    {
        path: SettingsPaths.integrations,
        element: <Integrations />,
    },
    {
        path: SettingsPaths.customDefinition,
        element: <CustomObjectDefinition />,
    },
    {
        path: SettingsPaths.objectTypes,
        element: <DataManagement />,
    },
    {
        path: SettingsPaths.yourData,
        element: <YourData />,
    },
];

// For reorganized nav - includes wildcard rerouter for moved routes
export const settingsRoute: RegalRouteObject = {
    path: AppPagePaths.settings,
    element: <SettingsRoutingPage />,
    hasNestedRoutes: true,
    children: [
        ...commonChildren,
        // Catch-all for old routes that moved to orchestration
        {
            path: "*",
            element: <SettingsRerouter />,
        },
    ],
};
